import { DateFormat, TagColor } from '@equip.health/ui';
import { DateTime } from 'luxon';

export const formattedDateFromISO = (
  date: string,
  format: DateFormat,
): string => {
  if (!date) return undefined;

  if (!format) return date;

  return DateTime.fromISO(date).toFormat(format);
};

/**
 * Convert Date to UTC midnight using only the year, month, and day portions of Date.
 *
 * @param date Date
 */
export const toUTCMidnight = (date: Date): Date => {
  return (
    date &&
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  );
};

/**
 * Convert Date to a UTC midnight date string in "YYYY-MM-DD" format.
 *
 * @param date Date
 * @returns string formatted as "YYYY-MM-DD"
 */
export const formatDateToUTCString = (date: Date): string => {
  if (!date) return '';

  const utcDate = toUTCMidnight(date);
  return utcDate.toISOString().split('T')[0]; // "YYYY-MM-DD"
};

/**
 * Convert Date to local midnight using only the UTC year, month, and day portions of Date.
 *
 * @param date Date
 */
export const toLocalMidnight = (date: Date): Date => {
  return (
    date &&
    new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
};

/**
 * Return the latter of two dates
 * @param a Date
 * @param b Date
 */
export const laterOf = (a: Date, b: Date): Date => {
  if (a && b) {
    return a > b ? a : b;
  }
  return a ? a : b;
};

/**
 * Return the earlier of two dates
 * @param a Date
 * @param b Date
 */
export const earlierOf = (a: Date, b: Date): Date => {
  if (a && b) {
    return a < b ? a : b;
  }
  return a ? a : b;
};

export const isDateTodayOrInPast = (dateStr: string): boolean => {
  if (!dateStr) return false;
  const inputDate = DateTime.fromISO(dateStr).startOf('day');
  const now = DateTime.now().startOf('day');
  return inputDate <= now;
};

/**
 * Checks if two dates are in the same year.
 * @param {string} date1 - The first date (ISO string).
 * @param {string} date2 - The second date (ISO string).
 * @returns {boolean} True if the two dates are in the same year, false otherwise.
 */
export const areDatesInSameYear = (date1: string, date2: string): boolean => {
  return (
    DateTime.fromISO(date1).isValid &&
    DateTime.fromISO(date2).isValid &&
    DateTime.fromISO(date1).year === DateTime.fromISO(date2).year
  );
};

/**
 *  The date string might have a space instead of a 'T', which fromISO handles.
 *   We normalize both dates to the start of the day to compare calendar days.
 * @param x - number of days
 * @param date - ISO date string
 * @returns
 */
export const hasBeenXOrMoreDaysSinceDate = (x: number, date: string) => {
  if (!date) {
    return false;
  }
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return false;
  }
  const dayOfDate = parsedDate.startOf('day');
  const now = DateTime.now().startOf('day');
  const daysSinceCreated = now.diff(dayOfDate, 'days').days;
  return daysSinceCreated >= x;
};

export const formatTimeRange = (
  startDateString?: string,
  durationInMinutes?: number,
  zone: 'utc' | 'local' = 'local',
) => {
  if (!startDateString || !durationInMinutes) return '';
  const start = DateTime.fromISO(startDateString, { zone });
  if (!start.isValid) return '';
  const end = start.plus({ minutes: durationInMinutes });
  const formatTime = (dt: DateTime) => dt.toFormat('h:mm').replace(':00', '');
  const getMeridiem = (dt: DateTime) => dt.toFormat('a').toLowerCase();
  const startLabel = formatTime(start);
  const endLabel = formatTime(end);
  const startPeriod = getMeridiem(start);
  const endPeriod = getMeridiem(end);
  return startPeriod === endPeriod
    ? `${startLabel} - ${endLabel} ${startPeriod}`
    : `${startLabel} ${startPeriod} - ${endLabel} ${endPeriod}`;
};

export const formatDateRange = (startDate?: string, endDate?: string) => {
  const start = startDate && DateTime.fromISO(startDate);
  const end = endDate && DateTime.fromISO(endDate);

  if (!start?.isValid) return '';
  if (!end?.isValid) return start.toFormat(DateFormat.shortMonthDayYear);

  const format = DateFormat.shortMonthDayYear;

  return start.year === end.year
    ? `${start.toFormat('LLL d')} - ${end.toFormat(format)}`
    : `${start.toFormat(format)} - ${end.toFormat(format)}`;
};

type DateInput = Date | string | null | undefined;

const normalizeDate = (value: DateInput): number | null => {
  if (!value) return null;

  const time =
    value instanceof Date ? value.getTime() : new Date(value).getTime();

  return Number.isNaN(time) ? null : time;
};

export const wasDateChanged = (
  initialDate: DateInput,
  currentDate: DateInput,
): boolean => {
  const initialTime = normalizeDate(initialDate);
  const currentTime = normalizeDate(currentDate);
  if (currentTime === null) return false;
  return initialTime !== currentTime;
};

export const isoToLocalDateTime = (isoString: string): DateTime =>
  DateTime.fromISO(isoString, { zone: 'utc' }).toLocal();

export const getDateTagProps = (isoDate: string | undefined) => {
  if (!isoDate) return undefined;

  const parsed = DateTime.fromISO(isoDate);
  if (!parsed.isValid) return undefined;

  const dateDay = parsed.startOf('day');
  const today = DateTime.now().startOf('day');

  if (dateDay < today) {
    return { color: TagColor.red, id: isoDate, value: 'Past' };
  }

  const diff = dateDay.diff(today, 'days');
  if (diff.days > 28) {
    return undefined;
  }

  const weeksLeft = Math.max(0, Math.floor(diff.days / 7));

  return {
    color: TagColor.orange,
    id: isoDate,
    value: `${weeksLeft} week${weeksLeft !== 1 ? 's' : ''} left`,
  };
};

export const unixTimestampToDate = (
  unixTimestampSeconds: number | null | undefined,
): Date | null => {
  if (unixTimestampSeconds == null) return null;
  return new Date(unixTimestampSeconds * 1000);
};

export const formatDateTimeInTimezone = (
  dateStr: string,
  timeStr: string,
  timezone: string,
): string => {
  const normalizedTime =
    timeStr.split(':').length === 2 ? `${timeStr}:00` : timeStr;
  const dt = DateTime.fromFormat(
    `${dateStr} ${normalizedTime}`,
    'yyyy-MM-dd HH:mm:ss',
    { zone: timezone },
  );
  return dt.isValid ? dt.toISO() : '';
};

export const roundToNext5Minutes = (dt: DateTime): DateTime => {
  const minutesToAdd = (5 - (dt.minute % 5)) % 5;
  return dt.plus({ minutes: minutesToAdd }).startOf('minute');
};

export const parseTimeString = (timeStr: string): [number, number, number] => {
  const [hour, minute, second = '0'] = timeStr.split(':');
  return [hour, minute, second].map((n) => Number.parseInt(n, 10)) as [
    number,
    number,
    number,
  ];
};

export const getEndDateMinDate = (startDate: string): Date | undefined => {
  if (!startDate) return undefined;
  const parsedStartDate = DateTime.fromFormat(startDate, 'yyyy-MM-dd');
  const minEndDate = parsedStartDate.plus({ days: 1 }).startOf('day');
  const today = DateTime.now().startOf('day');
  return minEndDate > today ? minEndDate.toJSDate() : today.toJSDate();
};

export const formatRelativeTime = (timestamp: string): string => {
  const emptyString = '';
  if (!timestamp) return emptyString;

  const dateTime = DateTime.fromISO(timestamp);
  if (!dateTime.isValid) return emptyString;

  const now = DateTime.now();
  const diffInHours = Math.floor(now.diff(dateTime, 'hours').hours);

  if (diffInHours < 0) return emptyString;

  // Less than 24 hours: show "x hours ago"
  if (diffInHours < 24) {
    if (diffInHours === 0) return 'less than 1 hour ago';
    if (diffInHours === 1) return '1 hour ago';
    return `${diffInHours} hours ago`;
  }

  // 24 hours or more: show "x days ago"
  const diffInDays = Math.floor(now.diff(dateTime, 'days').days);
  if (diffInDays === 1) return '1 day ago';
  if (diffInDays > 365) return 'Over a year ago';
  return `${diffInDays} days ago`;
};
